import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { get } from "../http/functions";
import { parseBackendToClientFriendlyForms } from "./functions";
import { generateExportMutationFn } from "./endpoints";
import { updateFormStatus } from "./thunks";

const getFormQueryFn = async ({ formType, id, ...options }) => {
  try {
    const { data, error: _error } = await get(
      `/forms/${formType}/${id}`,
      options
    );
    if (data?.id === id) {
      return { data };
    }

    throw (
      _error || {
        status: 404,
        data: "Form not found",
      }
    );
  } catch (error) {
    return error?.error ? error : { error };
  }
};

const getAllFormsQueryFn = async ({ year }) => {
  try {
    const { data, error: _error } = await get(`/forms?year=${year}`);
    if (data) {
      const validatedForms = parseBackendToClientFriendlyForms(data);
      return { data: validatedForms };
    }

    throw (
      _error || {
        status: 404,
        data: "Form not found",
      }
    );
  } catch (error) {
    return error?.error ? error : { error };
  }
};

export const createTagHash = ({ formType, id }) => {
  return `${formType}-${id}`;
};

export const formApi = createApi({
  reducerPath: "formApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["Form"],
  endpoints: (builder) => ({
    getForm: builder.query({
      queryFn: getFormQueryFn,
      providesTags: (result, error, { formType, id }) => {
        const tags = [{ type: "Form", id: createTagHash({ formType, id }) }];
        return tags;
      },
    }),
    getAllForms: builder.query({
      queryFn: getAllFormsQueryFn,
    }),
    generateExport: builder.mutation({
      queryFn: generateExportMutationFn,
    }),    
    updateFormStatus: builder.mutation({
      queryFn: async (args, api) => {
        try {
          const result = await api.dispatch(updateFormStatus(args)).unwrap()
          if(!result?.id || !result.formType) {
            throw new Error(`Unexpected response from update form status API`)
          }
          return { data: result }
        } catch (error) {
          return { error }
        }
      },
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        try {
          const { data: updatedForm } = await queryFulfilled
          dispatch(
            formApi.util.updateQueryData('getForm', 
              { formType: args.formType, id: args.identifier }, 
              (draft) => {
                const { formStatus, id, sk } = updatedForm
                const correctReduxMatch = draft?.sk === sk && draft?.id === id
                if(id && formStatus && correctReduxMatch) {
                  draft.status = formStatus
                }
              }
            )
          )
        } catch (error) {
          console.error(error)
        }
      }
    }),
  }),
});

export const { useGetFormQuery, useGetAllFormsQuery, useGenerateExportMutation, useUpdateFormStatusMutation } = formApi;
